import * as React from "react";
import {defineMessages, FormattedMessage, injectIntl} from "react-intl";
import {Link} from "react-router-dom";
import {ColumnsDescription, ColumnWidth} from "../common/ui/table/ControlRoomColumns";
import {createBasicTableComponent} from "../common/ui/table/ControlRoomTable";
import {PagedTableConfig} from "../common/ui/table/PagedTable";
import {DateUtil} from "../common/util/DateUtil";
import {actions} from "./actions";
import {Product} from "./model";
import {ProductRemoveButton} from "./ProductRemoveButton";
import {selectors} from "./selectors";
import {connect} from "react-redux";
import {selectors as pagedDataSelectors} from "../common/util/pagination/PaginationUtil";
import {sortItems, SortSettings} from "../common/ui/table/model";
import {COLUMN_SORT_SETTINGS_PRODUCTS, localRepository} from "../common/LocalRepository";

const config: PagedTableConfig = {
  canMultiSelect: true,
  noItemsFoundMessage: <FormattedMessage id="studio.products.product-list.no-products-found"
                                         defaultMessage="No products found."/>,
};

const MESSAGES = defineMessages({
  removeTooltip: {
    id: "studio.products.product-list.remove-tooltip",
    defaultMessage: "Remove {title}",
  },
});

let currentOnSortChange: ((colKey: string) => void) | null = null;

const makeHeader = (label: React.ReactNode, columnKey: string) => {
  let indicator = "";
  const sortSettings: SortSettings = localRepository.getOptions<SortSettings>(COLUMN_SORT_SETTINGS_PRODUCTS);
  if (sortSettings?.column === columnKey) {
    indicator = sortSettings.direction.startsWith("asc") ? " ▲" : " ▼";
  }

  return (
      <button
          type="button"
          onClick={() => currentOnSortChange && currentOnSortChange(columnKey)}
          style={{background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer"}}
          aria-label={`Sort by ${String(columnKey)}`}
      >
        <div className={"columnLabel"} style={{display: "inline-flex"}}>
          {label}
          <div className={"sortIndicator"} id={`sortIndicator_${columnKey}`}>{indicator}</div>
        </div>
      </button>
  );
}

const columns: ColumnsDescription<Product> = {
  title: {
    header: makeHeader(<FormattedMessage id="studio.products.product-list.title" defaultMessage="Title"/>, "title"),
    cellContent: (product) => <Link to={`/products/${product.id}`}>{product.title}</Link>,
    cellTooltip: (product) => product.title,
    columnWidth: ColumnWidth.flex(2),
  },
  keywords: {
    header: makeHeader(<FormattedMessage id="studio.products.product-list.keywords" defaultMessage="Keywords"/>,
        "keywords"),
    cellContent: (product) => product.keywords.join(", "),
    cellTooltip: (product) => product.keywords.join(", "),
    columnWidth: ColumnWidth.flex(1),
  },
  creationTime: {
    header: makeHeader(<FormattedMessage id="studio.products.product-list.date-created" defaultMessage="Date created"/>,
        "creationTime"),
    cellContent: (product) => DateUtil.formatDateTime(product.creationTime),
    cellTooltip: (product) => DateUtil.formatDateTime(product.creationTime),
    columnWidth: ColumnWidth.DATETIME_COLUMN_WIDTH,
  },
  remove: {
    header: <FormattedMessage id="studio.products.product-list.remove" defaultMessage="Remove"/>,
    cellContent: (product) => <ProductRemoveButton productId={product.id}/>,
    cellTooltip: (product, intl) => intl.formatMessage(MESSAGES.removeTooltip, {title: product.title}),
    columnWidth: ColumnWidth.REMOVE_COLUMN_WIDTH,
  },
};

export const ProductListComponent = createBasicTableComponent(columns);

function valueForColumn(item: Product, columnKey?: string | null) {
  switch (columnKey) {
  case "title":
    return (item.title || "").toString().toLowerCase();
  case "keywords":
    return (item.keywords || "").toString().toLowerCase();
  case "creationTime":
    const t = (item as any).creationTime;
    const num = typeof t === "number" ? t : Date.parse(t);
    return Number.isNaN(num) ? 0 : num;
  default:
    return "";
  }
}

const ConnectedProductsList = connect(
    (state: any, ownProps: any) => {
      const pagedState = selectors.getPagedState(state) || ({} as any);
      const rawItems: Product[] = pagedDataSelectors.getAllLoadedData<Product>(pagedState) || [];
      const sorted = sortItems(rawItems, valueForColumn, ownProps?.sortSettings);
      return {
        isPaged: true,
        canMultiSelect: config.canMultiSelect,
        noItemsFoundMessage: config.noItemsFoundMessage,
        data: sorted,
        pageSize: pagedDataSelectors.getPageSize(pagedState),
        pages: pagedDataSelectors.getAllPages<Product>(pagedState),
        isAllDataLoaded: pagedDataSelectors.isAllPagesLoaded(pagedState),
      };
    },
    (dispatch: any) => ({
      loadPage: (pageNumber: number) => dispatch(actions.requestPage(pageNumber)),
    })
)(injectIntl(ProductListComponent));

export const ProductList = (props: any) => {
  currentOnSortChange = props.onSortChange || null;
  return <ConnectedProductsList {...props} />;
};
